/* eslint-disable */
import { useState, useCallback, useRef, memo } from "react";
import { SlArrowLeft, SlArrowRight } from "react-icons/sl";
import { getS3Thumb } from "../../utils/utilityFunctions";

// Defined outside component — never re-created on re-render
const NextArrow = memo(({ onClick }) => (
  <div
    className="absolute right-2 h-fit bg-[#FFFFFF80] rounded-sm p-1 top-1/2 -translate-y-1/2 cursor-pointer z-10"
    onClick={(e) => { e.stopPropagation(); onClick(); }}
  >
    <SlArrowRight className="text-[#000]" />
  </div>
));
NextArrow.displayName = "NextArrow";

const PrevArrow = memo(({ onClick }) => (
  <div
    className="absolute left-2 h-fit bg-[#FFFFFF80] rounded-sm p-1 top-1/2 -translate-y-1/2 cursor-pointer z-10"
    onClick={(e) => { e.stopPropagation(); onClick(); }}
  >
    <SlArrowLeft className="text-[#000] text-xl" />
  </div>
));
PrevArrow.displayName = "PrevArrow";

/**
 * SmartCarousel — replaces react-slick for listing cards.
 *
 * Key behaviours:
 * - Only the ACTIVE slide image is in the DOM; others are skeleton divs.
 *   → Eliminates ~5–6 unnecessary image requests per card on initial load.
 * - On swipe/arrow click the next image is prefetched immediately.
 * - priority=true sets eager loading hints for above-fold cards.
 * - CSS opacity transition — zero layout reflow during slide changes.
 * - Touch swipe supported natively.
 * - Native loading="lazy" handles below-fold deferral on all devices.
 */
function SmartCarousel({ images = [], children, className = "", priority = false }) {
  // Pre-compute thumbnail URLs once — falls back to original on onError
  const thumbs = images.map(getS3Thumb);
  const [activeIdx, setActiveIdx] = useState(0);
  // Tracks which indices have been "unlocked" for loading (active + prefetched)
  const [loaded, setLoaded] = useState(() => new Set([0]));
  const touchStartX = useRef(null);
  const count = images.length;

  const goTo = useCallback((rawIdx) => {
    const next = ((rawIdx % count) + count) % count;
    setActiveIdx(next);
    setLoaded((prev) => {
      const s = new Set(prev);
      s.add(next);
      s.add((next + 1) % count); // prefetch the following slide
      return s;
    });
  }, [count]);

  const handleTouchEnd = useCallback((e) => {
    if (touchStartX.current === null) return;
    const delta = touchStartX.current - e.changedTouches[0].clientX;
    if (Math.abs(delta) > 40) {
      delta > 0 ? goTo(activeIdx + 1) : goTo(activeIdx - 1);
    }
    touchStartX.current = null;
  }, [activeIdx, goTo]);

  if (!count) return null;

  // Always load — VirtuosoGrid already ensures only viewport items are in the DOM.
  // Native loading="lazy" handles network deferral on browsers that support it.
  const canLoad = true;

  if (count === 1) {
    return (
      <div className="w-full relative">
        {canLoad ? (
          <img
            src={thumbs[0]}
            onError={(e) => { e.currentTarget.onerror = null; e.currentTarget.src = images[0]; }}
            alt=""
            loading={priority ? "eager" : "lazy"}
            fetchPriority={priority ? "high" : "auto"}
            decoding={priority ? "sync" : "async"}
            className={`w-full h-[260px] object-cover bg-gray-200 ${className}`}
          />
        ) : (
          <div className="w-full h-[260px] bg-gray-200 animate-pulse" />
        )}
        {children}
        <div className="min-h-[6.4px] w-full" />
      </div>
    );
  }

  return (
    <div
      className="w-full relative overflow-hidden"
      onTouchStart={(e) => { touchStartX.current = e.touches[0].clientX; }}
      onTouchEnd={handleTouchEnd}
    >
      <div className="relative w-full h-[260px] isolate">
        {images.map((src, idx) => {
          const isActive = idx === activeIdx;
          const shouldLoad = canLoad && loaded.has(idx);
          return (
            <div
              key={idx}
              className={`absolute inset-0 transition-opacity duration-200 ${isActive ? "opacity-100 z-[1]" : "opacity-0 z-0 pointer-events-none"
                }`}
              aria-hidden={!isActive}
            >
              {shouldLoad ? (
                <img
                  src={thumbs[idx]}
                  onError={(e) => { e.currentTarget.onerror = null; e.currentTarget.src = src; }}
                  alt=""
                  loading={priority && idx === 0 ? "eager" : "lazy"}
                  fetchPriority={priority && idx === 0 ? "high" : "auto"}
                  decoding="async"
                  className={`w-full h-[260px] object-cover bg-gray-200 ${className}`}
                />
              ) : (
                <div className="w-full h-[260px] bg-gray-200 animate-pulse" />
              )}
            </div>
          );
        })}
      </div>

      {children}

      <PrevArrow onClick={() => goTo(activeIdx - 1)} />
      <NextArrow onClick={() => goTo(activeIdx + 1)} />

      {/* Windowed pill indicator — max 5 dots, active = pill, edges = smaller */}
      {(() => {
        const MAX = 5;
        const start = count <= MAX ? 0 : Math.max(0, Math.min(activeIdx - 2, count - MAX));
        const window = Array.from({ length: Math.min(count, MAX) }, (_, i) => start + i);
        return (
          <div className="absolute bottom-2.5 left-1/2 -translate-x-1/2 flex items-center gap-[5px] z-10 pointer-events-none drop-shadow-sm">
            {window.map((idx) => {
              const dist = Math.abs(idx - activeIdx);
              return (
                <div
                  key={idx}
                  className={`rounded-full transition-all duration-200 ${dist === 0
                    ? "w-3.5 h-1.5 bg-white"
                    : dist === 1
                      ? "w-1.5 h-1.5 bg-white/70"
                      : "w-1 h-1 bg-white/45"
                    }`}
                />
              );
            })}
          </div>
        );
      })()}
    </div>
  );
}

export default memo(SmartCarousel);
